import { MindspaceRole } from "~/graphql/codegen/graphql";
import {
  managesInEngine,
  showsTransformationNav,
  type SpaceTransformationDashboard,
} from "./transformation-dashboard";

// FOX-6291 Part A: the Organisation page moves into the Transformation Engine
// on one switch day (`ORGANISATION_SWITCH_DAY`, read server-side in
// organisation-switch.server.ts). Everything Part A shows derives from that
// date. Pure, so client and server share it.

/** The pre-switch notice shows for this many days before the switch day. */
export const PRE_SWITCH_NOTICE_DAYS = 14;

/** The Organization item stays, marked "Moved", for this many days after it. */
export const MOVED_ITEM_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;
const SWITCH_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A strict `YYYY-MM-DD` that is a real calendar day, as that day's 00:00 UTC;
 * anything else (unset, blank, a time, another format, 2026-02-30) is null.
 */
export function parseSwitchDay(raw: string | undefined | null): Date | null {
  const match = SWITCH_DAY_RE.exec((raw ?? "").trim());
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  // Date.UTC rolls an impossible day over (Feb 30 → Mar 2): reject it.
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

/** `[switchDay - 14 days, switchDay)`: start inclusive, end exclusive. */
export function inPreSwitchWindow(switchDay: Date, now: Date): boolean {
  const end = switchDay.getTime();
  const start = end - PRE_SWITCH_NOTICE_DAYS * DAY_MS;
  const at = now.getTime();
  return at >= start && at < end;
}

/**
 * `before` the switch day (or with none), the Moved `window` of
 * [switchDay, switchDay + 30 days), then `after`.
 */
export type SwitchPhase = "before" | "window" | "after";

export function switchPhase(switchDay: Date | null, now: Date): SwitchPhase {
  if (!switchDay) return "before";
  const at = now.getTime();
  if (at < switchDay.getTime()) return "before";
  return at < switchDay.getTime() + MOVED_ITEM_DAYS * DAY_MS ? "window" : "after";
}

/** The next moment the phase changes (switch day, then the window's end), or null. */
export function nextSwitchBoundary(switchDay: Date | null, now: Date): Date | null {
  if (!switchDay) return null;
  const end = new Date(switchDay.getTime() + MOVED_ITEM_DAYS * DAY_MS);
  if (now.getTime() < switchDay.getTime()) return switchDay;
  return now.getTime() < end.getTime() ? end : null;
}

type ViewerPermission =
  | { role: MindspaceRole; isChampion: boolean; isCoach: boolean }
  | null
  | undefined;

export function showsPreSwitchNotice({
  inWindow,
  dashboard,
  permission,
}: {
  inWindow: boolean;
  /** The engine link; only one that manages there means the page moves. */
  dashboard: SpaceTransformationDashboard | null | undefined;
  permission: ViewerPermission;
}): boolean {
  // A learner-only link never moves the Organisation page (plan
  // 261104_learner-dashboard-link, review F1), so it is never announced.
  if (!inWindow || !managesInEngine(dashboard ?? null)) return false;
  // FOX-6291 announces the move to admins and champions only. Coaches wait on
  // the coach-access question (Emma), and a plain member who opens
  // /organization directly is not part of the move, so neither is told.
  return isAdminOrChampion(permission);
}

export function isAdminOrChampion(permission: ViewerPermission): boolean {
  return !!permission && (permission.role === MindspaceRole.Admin || permission.isChampion);
}

/**
 * Whether this viewer has moved to the Transformation Engine: from the switch
 * day, an admin or champion who has the engine in their nav. Anyone else
 * (coach-only seats, members, spaces with no engine link for them) keeps
 * today's Organisation page.
 */
export function switchesOver({
  phase,
  permission,
  hasEngineNav,
}: {
  phase: SwitchPhase;
  permission: ViewerPermission;
  hasEngineNav: boolean;
}): boolean {
  return phase !== "before" && hasEngineNav && isAdminOrChampion(permission);
}

/**
 * The moved page at `/organization`: in the window, in a space that existed
 * before the switch day. Spaces created since never had the Organisation page
 * and go straight to the engine. An unknown creation time counts as existing.
 */
export function showsMovedPage({
  phase,
  switchDay,
  spaceCreatedAt,
}: {
  phase: SwitchPhase;
  switchDay: Date | null;
  spaceCreatedAt: Date | null;
}): boolean {
  if (phase !== "window" || !switchDay) return false;
  return !spaceCreatedAt || spaceCreatedAt.getTime() < switchDay.getTime();
}

/**
 * The "Moved" Organization item: where the moved page shows, for a switched
 * viewer who has not opened it from the item yet.
 */
export function showsMovedItem({
  switchedOver,
  seen,
  ...page
}: {
  switchedOver: boolean;
  phase: SwitchPhase;
  switchDay: Date | null;
  spaceCreatedAt: Date | null;
  seen: boolean;
}): boolean {
  return switchedOver && !seen && showsMovedPage(page);
}

/**
 * The two sidebar entries this viewer gets. `engine`: `legacy` is today's
 * Transformation item, `engine` the Transformation Engine item (new tab).
 * `organization`: `legacy` is today's Organization item, `moved` the Moved one.
 * `legacyOrganization` is today's own gate for the Organization item, which
 * still bounds it: the switch never adds an item that gate would not show.
 */
export function organisationNav({
  phase,
  switchDay,
  spaceCreatedAt,
  seen,
  permission,
  dashboard,
  legacyOrganization,
}: {
  phase: SwitchPhase;
  switchDay: Date | null;
  spaceCreatedAt: Date | null;
  seen: boolean;
  permission: ViewerPermission;
  /** Undefined while the lookup is pending. */
  dashboard: SpaceTransformationDashboard | null | undefined;
  legacyOrganization: boolean;
}): { engine: "none" | "legacy" | "engine"; organization: "none" | "legacy" | "moved" } {
  const hasEngineNav = showsTransformationNav(dashboard ?? null);
  const engine = !hasEngineNav ? "none" : phase === "before" ? "legacy" : "engine";
  // The Organisation page moves only for someone who manages in the engine.
  const managesThere = managesInEngine(dashboard ?? null);
  if (!legacyOrganization) return { engine, organization: "none" };
  // From the switch day, an admin or champion may be about to switch: show no
  // Organization item until the lookup says which one, rather than today's
  // item that then turns into the Moved one or vanishes.
  if (dashboard === undefined && phase !== "before" && isAdminOrChampion(permission)) {
    return { engine, organization: "none" };
  }
  const switchedOver = switchesOver({ phase, permission, hasEngineNav: managesThere });
  if (!switchedOver) return { engine, organization: "legacy" };
  const moved = showsMovedItem({ switchedOver, phase, switchDay, spaceCreatedAt, seen });
  return { engine, organization: moved ? "moved" : "none" };
}
